/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For mor information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(module.exports, {
    namespace: "ua.a.incenter",
    title: "Link: Center Element",
    description: "Scrolls the specified element to the center of the screen, on a click.",
    fields: {
        goto: {
            type: "id",
            description: "The ID to the anchor or element to scroll to the center of the screen.",
            required: true,
            title: "Goto"
        }
    },
    passthrough: {
        except: ["goto"]
    }
});

var events = require("../../../uam/events.js");

/**
 * Adds a anchor link tag to the page.
 * @param {*} options The json objects and properties to render.
 * @returns The final renderalbe object, dom or ua/json.
 */
exports.render = async function (options) {

    var anc = {
        n: "a",
        c: "ua-a-incenter",
        href: options.goto
    };

    if (options.inner) {
        anc.i = options.inner;
    }

    return anc;

}

/* ===========================================================================
 * EVERY same-document `#` link centres its target — not just `.ua-a-incenter`.
 *
 * WHY THE CLASS IS NO LONGER THE GATE. The class only ever landed on what an
 * author remembered to opt in (footnote markers, ua.footnote, and the ua.navbar
 * burger — whose href is a bare "#", so it was a no-op). Every ordinary in-page
 * link — a hero's "see below", a document's own table of contents, a footnote
 * back-link — fell through to the browser's default jump, which drops the
 * target at the TOP of the scrollport, where sticky chrome covers it and the
 * eye has nothing above it to read from. Centring is what we want for all of
 * them, so the delegated handler now matches `a[href]`; the class is kept only
 * as a compatible marker (`render()` still emits it).
 *
 * OPTING OUT. `[data-ua-incenter="off"]` (or `.ua-a-incenter-off`) on the link
 * or any ancestor. Links whose hash is a widget handle rather than a
 * destination are excluded outright — see NOT_A_DESTINATION.
 *
 * THE ATTENTION CUE IS REDUCED, NOT REMOVED (founder ruling, 2026-07-24:
 * "you can reduce the highlight for everyone"), and the reduction applies to
 * every tenant — there is no per-tenant conditional anywhere in this file.
 *   - GONE PERMANENTLY: the full-screen blurred-black overlay that used to
 *     cover the page for 500ms of every jump. agents.md §4 forbids darkening
 *     content, so it is not coming back for anyone.
 *   - REDUCED: `anim-highlight`'s 1s primary-coloured background + 2px outline
 *     flash is now a single faint grey ring that fades off in 700ms and paints
 *     nothing under reduced motion. See `.ua-a-incenter-highlight` in
 *     uat/scss/animations.scss, and CueTarget() below for when it fires.
 *
 * WHY WE ANIMATE THE SCROLL OURSELVES. `scroll-behavior: smooth` (Bootstrap's
 * reboot sets it on `:root` for everyone who has not asked for reduced motion)
 * is not reliable here: measured on egt.studio 2026-07-24, both the browser's
 * OWN fragment navigation and `window.scrollTo({behavior:"smooth"})` start and
 * are then abandoned a few pixels in — /forge's "See what we forge" set the
 * hash and travelled 3px; a footnote marker on /platform travelled 10px. So we
 * force `scroll-behavior: auto` for the duration and drive each step from rAF
 * on a TIME base, which cannot be abandoned half-way. The destination is
 * recomputed every frame (reveal-on-scroll and late media move the target while
 * we travel) and set exactly at the end, with a timer backstop in case rAF is
 * throttled. A real user gesture during the travel cancels it — the reader
 * always wins.
 * ========================================================================= */

/* A link may own its `#` for something other than travel. Hijacking these
 * breaks the widget: Bootstrap collapse/tab/dropdown/modal/carousel toggles,
 * the ua.navbar burger, and anything ARIA-declared as a tab. */
var NOT_A_DESTINATION = '[data-bs-toggle],[data-bs-target],[data-toggle],[ua-navbar-menu],[role="tab"]';
var OPT_OUT = '[data-ua-incenter="off"],.ua-a-incenter-off';

/* Fixed/sticky page chrome lives at the top of the viewport; a bounded list of
 * the elements that are ever page chrome keeps the measurement cheap. Only used
 * when the page has not declared `scroll-padding-top` itself. */
var CHROME_SELECTOR = "header,nav,.ua-navbar,.navbar,.ua-appbar,.fixed-top,.sticky-top";

/* Gestures that mean "I am steering now" — they cancel an in-flight travel. */
var RELEASE_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"];

/* How long a travel takes. Long enough to read as movement (so the reader
 * keeps their bearings), short enough that nobody waits for it. */
var TRAVEL_MS = 460;

/* The landing cue. CUE_MS must match the animation duration declared for
 * `.ua-a-incenter-highlight` in uat/scss/animations.scss — it only controls
 * when the class is taken back off, so a mismatch cannot cut the animation
 * short, but keep them in step. */
var CUE_CLASS = "ua-a-incenter-highlight";
var CUE_MS = 700;
var cueTimer = 0;

function CanQueryDocument() {
    return (typeof document != "undefined") && !!document.body
        && (typeof document.body.addEventListener == "function");
}

function PrefersReducedMotion() {
    /* Read at call time, not module load: a reader can change the OS setting
     * without reloading the page. */
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
}

function IsPlainLeftClick(event) {
    if (event.defaultPrevented) {
        return false;
    }

    if (typeof event.button == "number" && event.button !== 0) {
        return false;
    }

    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
}

function IsInPageDestination(link) {
    if (!link || typeof link.closest != "function") {
        return false;
    }

    if (link.hasAttribute("download")) {
        return false;
    }

    var target = (link.getAttribute("target") || "").trim().toLowerCase();
    if (target && target != "_self") {
        return false;
    }

    /* MATCHES, NOT CLOSEST — and this is the whole reason the feature appeared
     * to do nothing. These attributes always sit on the toggle ITSELF, so an
     * ancestor walk is not just unnecessary, it is wrong: every Universe page
     * wraps its content in `<main data-bs-spy="scroll" data-bs-target="…">`
     * (views/pages.ejs), so `closest("[data-bs-target]")` matched that wrapper
     * for EVERY link on EVERY page and excluded the lot. Verified on
     * egt.studio: with closest() the handler never fired once, and what looked
     * like working travel was the browser's own fragment navigation. */
    if (link.matches && link.matches(NOT_A_DESTINATION)) {
        return false;
    }

    /* closest() IS right here: an opt-out is meant to cover a whole region. */
    if (link.closest(OPT_OUT)) {
        return false;
    }

    return true;
}

events.on("interface.afterrender", async function () {

    if (!(CanQueryDocument())) {
        return;
    }

    if (document.body.getAttribute("ua-a-incenter-listener") == "true") {
        return;
    }
    document.body.setAttribute("ua-a-incenter-listener", "true");

    // ONE delegated listener for the whole document.
    document.body.addEventListener('click', function (event) {

        var link = (event.target && typeof event.target.closest == "function")
            ? event.target.closest('a[href]')
            : null;

        if (!link) {
            return;
        }

        if (!IsPlainLeftClick(event)) {
            return;
        }

        if (!IsInPageDestination(link)) {
            return;
        }

        var href = link.getAttribute('href');
        if (!(typeof href == "string")) {
            return;
        }

        if (!IsSamePageHashTarget(href)) {
            return;
        }

        var targetId = GetTargetIdFromHref(href);
        if (!targetId) {
            return;
        }

        var targetElement = document.getElementById(targetId);
        if (!targetElement) {
            return;
        }

        event.preventDefault();
        CenterScrollToElement(targetElement);
        MoveFocusToTarget(targetElement);

        /* Keep the URL honest — the default jump we just suppressed would have
         * written the hash, and a reader who copies the address bar expects the
         * section they are looking at. Use the History API (never the hash
         * setter) so we do not hand the browser a fragment navigation to fight
         * us over.
         *
         * pushState, NOT replaceState. The native jump we replaced PUSHES a
         * history entry, so Back returns the reader to where they were before
         * the jump; replaceState overwrote the pre-jump entry, so Back skipped
         * the page the reader arrived from (e.g. /platform → click a footnote →
         * Back wrongly landed on /reserve). This handler fires ONLY on an
         * explicit click — there is no scroll-spy writing the hash anywhere in
         * this file — so pushState cannot flood history on passive scroll.
         * Guard on a changed hash so clicking the same anchor twice does not
         * stack duplicate entries the reader must Back through (matching native
         * behaviour, which coalesces an unchanged same-page hash). */
        if (window.history) {
            var newHash = "#" + targetId;
            try {
                if (window.location.hash !== newHash
                    && typeof window.history.pushState == "function") {
                    window.history.pushState(null, "", newHash);
                } else if (typeof window.history.replaceState == "function") {
                    window.history.replaceState(null, "", newHash);
                }
            } catch (error) {
                /* opaque origin / file:// — the travel still happened */
            }
        }
    });
});

/**
 * Suppressing the default jump also suppresses the part of it nobody sees: the
 * browser moves focus (and the sequential-focus starting point) to the target,
 * which is the ENTIRE point of a skip link and is how a screen reader knows the
 * reader has arrived. Put it back. `preventScroll` is required — focus() would
 * otherwise scroll the target to the edge and undo the centring we just began.
 * @param {*} el The element that was jumped to.
 */
function MoveFocusToTarget(el) {
    if (!el || typeof el.focus != "function") {
        return;
    }

    /* A heading or a section is not focusable; make it programmatically
     * focusable without putting it in the tab order. */
    if (!el.hasAttribute("tabindex")) {
        var focusable = /^(a|area|button|input|object|select|textarea|summary|iframe)$/i;
        if (!focusable.test(el.tagName) && !el.isContentEditable) {
            el.setAttribute("tabindex", "-1");
        }
    }

    try {
        el.focus({ preventScroll: true });
    } catch (error) {
        /* older engines ignore the options bag; a plain focus would scroll, so
         * we would rather not focus at all than fight our own travel */
    }
}

function GetTargetIdFromHref(href) {
    if (typeof href != "string") {
        return "";
    }

    if (href == "#") {
        return "";
    }

    try {
        var resolved = new URL(href, window.location.href);
        if (!resolved.hash) {
            return "";
        }

        return decodeURIComponent(resolved.hash.substring(1));
    } catch (error) {
        if (href.includes("#")) {
            return href.split("#").pop();
        }

        return "";
    }
}

function NormalizePath(pathname) {
    if (!(typeof pathname == "string")) {
        return "/";
    }

    var path = pathname.trim();
    if (!path) {
        path = "/";
    }

    if (!path.startsWith("/")) {
        path = "/" + path;
    }

    if (path.endsWith("/")) {
        path += "index.html";
    }

    path = path.replace(/\/index\.html$/i, "/");
    path = path.replace(/\.html$/i, "");

    if (path.length > 1 && path.endsWith("/")) {
        path = path.substring(0, path.length - 1);
    }

    return path;
}

function IsSamePageHashTarget(href) {
    if (typeof href != "string") {
        return false;
    }

    var cleanHref = href.trim();
    if (!cleanHref || !cleanHref.includes("#")) {
        return false;
    }

    if (cleanHref.startsWith("#")) {
        return true;
    }

    try {
        var currentUrl = new URL(window.location.href);
        var targetUrl = new URL(cleanHref, window.location.href);

        if (currentUrl.origin != targetUrl.origin) {
            return false;
        }

        return NormalizePath(currentUrl.pathname) == NormalizePath(targetUrl.pathname);
    } catch (error) {
        return false;
    }
}

/**
 * The height of the page chrome pinned to the TOP of the scrollport, i.e. the
 * amount of the viewport a reader cannot actually read in.
 *
 * A page that declares `scroll-padding-top` on its scrolling element has
 * already told us this number — honour it and stop, so authors keep a single
 * source of truth and native jumps agree with us. Otherwise measure whatever
 * fixed/sticky chrome is currently covering the top edge, which is what makes
 * a top-aligned target land BELOW a sticky bar instead of under it.
 * @returns {number} pixels
 */
function TopInset() {
    var docEl = document.documentElement;

    var declared = parseFloat(window.getComputedStyle(docEl).scrollPaddingTop);
    if (!isNaN(declared) && declared > 0) {
        return declared;
    }

    var viewport = window.innerHeight || docEl.clientHeight || 0;
    var inset = 0;
    var candidates = document.querySelectorAll(CHROME_SELECTOR);
    for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        var cs = window.getComputedStyle(el);

        if (cs.position != "fixed" && cs.position != "sticky") {
            continue;
        }

        /* NOT VISIBLE, four ways — and all four are load-bearing. ua.navbar's
         * closed mobile sheet is `position:fixed; inset:0; opacity:0 !important;
         * pointer-events:none !important` with NO visibility/display change
         * (uat/scss/nav.scss `.ua-navbar-menu-mobile`), and it is in the DOM of
         * every page that has a navbar. Testing only display/visibility
         * measured it as a FULL-VIEWPORT chrome bar, which left no readable
         * area at all and sent every target to the top-align branch. */
        if (cs.display == "none" || cs.visibility == "hidden") {
            continue;
        }

        if (cs.opacity === "0" || cs.pointerEvents == "none") {
            continue;
        }

        var rect = el.getBoundingClientRect();

        /* Only chrome that is actually covering the top edge right now. */
        if (rect.height <= 0 || rect.top > 2 || rect.bottom <= 0) {
            continue;
        }

        /* A bar, not a takeover. Anything claiming more than half the viewport
         * is an open sheet/overlay, and reserving it would leave nothing to
         * centre in — the reader would rather we ignored it. */
        if (viewport > 0 && rect.height > (viewport / 2)) {
            continue;
        }

        if (rect.bottom > inset) {
            inset = rect.bottom;
        }
    }

    return inset;
}

function MaxScrollTop() {
    var docEl = document.documentElement;
    var viewport = window.innerHeight || docEl.clientHeight || 0;
    return Math.max(0, (docEl.scrollHeight || 0) - viewport);
}

function CurrentScrollTop() {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
}

/**
 * Where the document has to sit for `el` to read as CENTRED in the part of the
 * viewport the reader can actually see.
 *
 * A target TALLER than that area is TOP-ALIGNED instead: centring it would put
 * its own heading off the top of the screen, which is worse than the default we
 * are replacing. Top-alignment clears the chrome by `max(scroll-margin-top,
 * inset)` — MAX, never a sum, because an author who set `scroll-margin-top` to
 * clear a sticky bar would otherwise get that clearance twice.
 * @param {*} el The element to bring into view.
 * @returns {number} the scrollTop to land on, clamped to the document.
 */
function CenterScrollTopFor(el) {
    var box = MeasureTarget(el);
    var top;

    if (box.tall) {
        top = box.scrollTop + box.rect.top - Math.max(box.marginTop, box.inset);
    } else {
        top = box.scrollTop + box.rect.top + (box.rect.height / 2)
            - (box.inset + (box.readable / 2));
    }

    return Math.max(0, Math.min(MaxScrollTop(), Math.round(top)));
}

/**
 * The one measurement both the travel and the landing cue read, so they can
 * never disagree about whether a target fits on screen.
 * @param {*} el The target element.
 */
function MeasureTarget(el) {
    var cs = window.getComputedStyle(el);
    var marginTop = parseFloat(cs.scrollMarginTop) || 0;
    var marginBottom = parseFloat(cs.scrollMarginBottom) || 0;

    var inset = TopInset();
    var viewport = window.innerHeight || document.documentElement.clientHeight || 0;
    var readable = Math.max(1, viewport - inset);
    var rect = el.getBoundingClientRect();

    return {
        rect: rect,
        inset: inset,
        readable: readable,
        marginTop: marginTop,
        scrollTop: CurrentScrollTop(),
        tall: (rect.height + marginTop + marginBottom) >= readable
    };
}

/**
 * The landing cue: a faint ring that fades off the target once it has arrived.
 * Styling (and the reduced-motion opt-out) lives in uat/scss/animations.scss —
 * see the long note on `.ua-a-incenter-highlight` for why it is an outline.
 *
 * Skipped in two cases, both so the cue never becomes noise:
 *   - reduced motion, where the founder's instruction is no animation at all;
 *   - a target TALLER than the readable area, where a ring around something
 *     bigger than the screen would show up as two unexplained lines at the
 *     edges rather than as a thing being pointed at.
 * @param {*} el The element that was travelled to.
 */
function CueTarget(el) {
    if (!el || !el.classList) {
        return;
    }

    if (PrefersReducedMotion()) {
        return;
    }

    if (MeasureTarget(el).tall) {
        return;
    }

    if (cueTimer) {
        window.clearTimeout(cueTimer);
        cueTimer = 0;
    }

    var lit = document.querySelectorAll("." + CUE_CLASS);
    for (var i = 0; i < lit.length; i++) {
        lit[i].classList.remove(CUE_CLASS);
    }

    /* Re-adding a class an element already carries does NOT restart a CSS
     * animation, so clicking the same link twice would cue only once. Flushing
     * layout between the remove and the add is what re-arms it. */
    void el.offsetWidth;
    el.classList.add(CUE_CLASS);

    /* Take the class back off so the DOM does not accumulate state, and so the
     * next arrival is a fresh animation rather than a no-op. */
    cueTimer = window.setTimeout(function () {
        el.classList.remove(CUE_CLASS);
        cueTimer = 0;
    }, CUE_MS + 120);
}

/* The one travel in flight, if any. A second click replaces it. */
var travel = null;

function EndTravel(instance) {
    if (!instance || instance.done) {
        return;
    }

    instance.done = true;

    if (instance.raf && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(instance.raf);
    }

    if (instance.backstop) {
        window.clearTimeout(instance.backstop);
    }

    for (var i = 0; i < RELEASE_EVENTS.length; i++) {
        window.removeEventListener(RELEASE_EVENTS[i], instance.release);
    }

    /* Hand the stylesheet back its own scroll-behavior. Restoring the value we
     * found (rather than clearing it) keeps any inline value a page had set. */
    document.documentElement.style.scrollBehavior = instance.behavior;

    if (travel === instance) {
        travel = null;
    }
}

/**
 * Brings an element to the middle of the readable viewport.
 * @param {*} targetElement An element, or the id of one.
 * @returns {boolean} true when a travel was started (or already there).
 */
function CenterScrollToElement(targetElement) {

    if (typeof targetElement == "string") {
        targetElement = document.getElementById(targetElement);
    }

    if (!targetElement || typeof targetElement.getBoundingClientRect != "function") {
        return false;
    }

    /* A second click supersedes the first, and restores scroll-behavior before
     * we read it below. */
    EndTravel(travel);

    var docEl = document.documentElement;
    var behavior = docEl.style.scrollBehavior;
    docEl.style.scrollBehavior = "auto";

    var from = CurrentScrollTop();
    var to = CenterScrollTopFor(targetElement);

    if (PrefersReducedMotion() || Math.abs(to - from) < 2 || !window.requestAnimationFrame) {
        window.scrollTo(0, to);
        docEl.style.scrollBehavior = behavior;
        /* Nothing to wait for — cue where the reader already is. (Under reduced
         * motion CueTarget itself declines, which is the intended no-op.) */
        CueTarget(targetElement);
        return true;
    }

    var instance = { done: false, raf: 0, backstop: 0, behavior: behavior };
    instance.release = function () { EndTravel(instance); };
    travel = instance;

    for (var i = 0; i < RELEASE_EVENTS.length; i++) {
        window.addEventListener(RELEASE_EVENTS[i], instance.release, { passive: true });
    }

    /* rAF is throttled to nothing in a hidden tab; land the travel anyway. */
    instance.backstop = window.setTimeout(function () {
        if (instance.done) {
            return;
        }
        window.scrollTo(0, CenterScrollTopFor(targetElement));
        CueTarget(targetElement);
        EndTravel(instance);
    }, TRAVEL_MS + 400);

    var startedAt = 0;

    function step(now) {
        if (instance.done) {
            return;
        }

        if (!startedAt) {
            startedAt = now;
        }

        var t = Math.min(1, (now - startedAt) / TRAVEL_MS);
        var eased = 1 - Math.pow(1 - t, 3); /* ease-out cubic */

        /* Recompute every frame: reveal-on-scroll, webfonts and late media all
         * move the target underneath us while we travel. */
        var destination = CenterScrollTopFor(targetElement);
        window.scrollTo(0, Math.round(from + ((destination - from) * eased)));

        if (t < 1) {
            instance.raf = window.requestAnimationFrame(step);
            return;
        }

        window.scrollTo(0, CenterScrollTopFor(targetElement));
        /* Cue on ARRIVAL only. A travel that the reader cancelled by scrolling
         * ends through EndTravel/release instead, and deliberately gets no cue:
         * they are looking somewhere else now. */
        CueTarget(targetElement);
        EndTravel(instance);
    }

    instance.raf = window.requestAnimationFrame(step);
    return true;
}
